/** Small formatting helpers for the pod dashboard UI. */
import { DateTime } from 'luxon';

/** $22,550 (no cents). Returns an em-dash for null/undefined. */
export const formatUsd = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '—';
    return value.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
    });
};

/** $22,550/mo */
export const formatUsdPerMonth = (value: number | null | undefined): string =>
    value === null || value === undefined ? '—' : `${formatUsd(value)}/mo`;

/** "Jun 14, 2023" from an ISO date string. */
export const formatStartDate = (iso: string | null): string => {
    if (!iso) return '—';
    const dt = DateTime.fromISO(iso);
    return dt.isValid ? dt.toFormat('LLL d, yyyy') : '—';
};

/** "12 mo" / "24 mo". */
export const formatTerm = (months: number | null): string =>
    months === null || months === undefined ? '—' : `${months} mo`;

/**
 * Human "held for" duration between a start date and now (or a reference date).
 * Examples: "3 yr", "2 yr 4 mo", "9 mo", "< 1 mo".
 */
export const formatHeldFor = (iso: string | null, referenceIso?: string): string => {
    if (!iso) return '—';
    const start = DateTime.fromISO(iso);
    if (!start.isValid) return '—';
    const end = referenceIso ? DateTime.fromISO(referenceIso) : DateTime.local();
    const ref = end.isValid ? end : DateTime.local();
    if (ref < start) return '—';

    const diff = ref.diff(start, ['years', 'months']);
    const years = Math.floor(diff.years);
    const months = Math.floor(diff.months);

    if (years <= 0 && months <= 0) return '< 1 mo';
    if (years <= 0) return `${months} mo`;
    if (months <= 0) return `${years} yr`;
    return `${years} yr ${months} mo`;
};

/** Percent of total, rounded to a whole number. */
export const percentOf = (value: number, total: number): number =>
    total > 0 ? Math.round((value / total) * 100) : 0;
